import { useCallback, useContext, useMemo } from 'react';
import { DefenderChatResponse } from 'client-factories/MicrosoftConsumerSecurityCopilotClient';
import { toError } from 'common/utilities/ErrorHelpers';
import { localeIgnoreCaseEquals } from 'common/utilities/String';
import { ApiCallResultStatus } from 'store';
import { isApiCallSuccess } from 'store/apiCallResultStatus';

import { IChatHistoryItem } from '../Chat.types';
import { ChatContext } from '../ChatContextProvider';

export const useCopilot = () => {
  const {
    history,
    prompt,
    temperature,
    topP,
    seed,
    useExperimentalModel,
    artifactId,
    artifactType,
    setHistory,
    setPrompt,
    setTemperature,
    setTopP,
    setSeed,
    setExperimentalModel,
    setArtifactId,
    setArtifactType,
  } = useContext(ChatContext);

  const pushQuestion = useCallback(
    (question: string) => {
      const trimmedQuestion = question.trim();
      if (!trimmedQuestion) {
        return null;
      }

      const itemId = `${Date.now()}`;
      setHistory((prevHistory) => [...prevHistory, { question: trimmedQuestion, id: itemId, apiStatus: ApiCallResultStatus.LOADING }]);
      return itemId;
    },
    [setHistory]
  );

  const onResponseFailure = useCallback(
    (itemId: string, error: unknown) => {
      setHistory((prevHistory) => {
        const existingItemIndex = prevHistory.findIndex((item) => localeIgnoreCaseEquals(item.id, itemId));
        if (existingItemIndex >= 0) {
          const existingItem = prevHistory[existingItemIndex];
          const newHistory = [...prevHistory];
          const newItem: IChatHistoryItem = {
            ...existingItem,
            response: undefined,
            metadata: undefined,
            apiStatus: ApiCallResultStatus.FAILED,
            error: toError(error),
          };
          newHistory[existingItemIndex] = newItem;
          return newHistory;
        }

        return [...prevHistory];
      });
    },
    [setHistory]
  );

  const onResponseSuccess = useCallback(
    (itemId: string, response: DefenderChatResponse) => {
      const { message = '', metadata } = response;

      setHistory((prevHistory) => {
        const existingItemIndex = prevHistory.findIndex((item) => localeIgnoreCaseEquals(item.id, itemId));
        if (existingItemIndex >= 0) {
          const existingItem = prevHistory[existingItemIndex];
          const newHistory = [...prevHistory];
          const newItem: IChatHistoryItem = {
            ...existingItem,
            apiStatus: ApiCallResultStatus.SUCCESS,
            error: undefined,
            response: message,
            metadata,
          };
          newHistory[existingItemIndex] = newItem;
          return newHistory;
        }

        return [...prevHistory];
      });
    },
    [setHistory]
  );

  const clearHistory = useCallback(() => {
    setHistory([]);
  }, [setHistory]);

  const resolvedHistory = useMemo(() => history.filter((item) => isApiCallSuccess(item.apiStatus)), [history]);

  return {
    history,
    prompt,
    temperature,
    topP,
    seed,
    useExperimentalModel,
    resolvedHistory,
    artifactId,
    artifactType,
    pushQuestion,
    onResponseFailure,
    onResponseSuccess,
    setPrompt,
    setTemperature,
    setTopP,
    setSeed,
    clearHistory,
    setExperimentalModel,
    setArtifactId,
    setArtifactType,
  };
};
